// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/PaymentOut.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, vendorsAPI, accountsAPI, customersAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, Edit2, Printer } from "lucide-react";
import { printReceiptOut } from "../../../utils/InvoicePrint";
import JournalFilter, { emptyFilter, JournalFilterValues } from "./JournalFilter";
import AttachmentField from "../../../components/AttachmentField";
import { fileUrl } from "../../../utils/txnReport";
import { useTxnList, describeFilter } from "./useTxnList";
import TxnReportModal from "./TxnReportModal";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const partyName2 = (p: any) => `${p.first_name || ""} ${p.last_name || ""} ${p.company_name || ""}`.trim();

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

const vendorName = (v: any) =>
  `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim();

export default function PaymentOut() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [showReport, setShowReport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [vendors, setVendors]     = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [partyType, setPartyType] = useState<"vendor"|"customer">("vendor");
  const [cashAccs, setCashAccs] = useState<any[]>([]);
  const [filter, setFilter]     = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]       = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"confirmed">("all");

  const { list, loading, page, setPage, totalPages, totalAll, pendingCount, confirmedCount, reload } = useTxnList({
    fetcher: (p) => journalAPI.listPaymentsOut(p),
    respKey: "payments",
    filter, statusFilter,
  });

  const [form, setForm] = useState({
    date: todayGregorian(),
    vendor_id: "", customer_id: "", vendor_currency: "USD",
    payment_currency: "USD", payment_amount: "",
    exchange_rate: "1", account_id: "", account_name: "",
    notes: "", attachment: "",
  } as any);

  const loadMeta = useCallback(async () => {
    const [v, c, a] = await Promise.all([
      vendorsAPI.list(),
      customersAPI.list(),
      accountsAPI.getCashAccounts(),
    ]);
    if (v.success) setVendors(v.vendors || []);
    if (c.success) setCustomers(c.customers || []);
    if (a.success) setCashAccs(a.accounts || []);
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  const load = () => { loadMeta(); reload(); };

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const calcAmt = (amt: string, rate: string, cur: string) => {
    const a = parseFloat(amt || "0");
    const r = parseFloat(rate || "1");
    if (!a || !r) return 0;
    return cur === "USD" ? a / r : a * r;
  };

  const vendAmt     = calcAmt(form.payment_amount, form.exchange_rate, form.vendor_currency);
  const editVendAmt = editItem ? calcAmt(editItem.payment_amount, editItem.exchange_rate, editItem.vendor_currency) : 0;

  const validate = () => {
    if (partyType === "vendor" && !form.vendor_id) { showToast("فروشنده الزامی است", false); return false; }
    if (partyType === "customer" && !form.customer_id) { showToast("مشتری الزامی است", false); return false; }
    if (!form.payment_amount) { showToast("مبلغ الزامی است", false); return false; }
    if (!form.account_id)     { showToast("حساب پرداختی الزامی است", false); return false; }
    return true;
  };

  const buildSubmitData = (statusValue: "pending"|"confirmed") => {
    const vendor   = vendors.find((v: any) => String(v.id) === form.vendor_id);
    const customer = customers.find((c: any) => String(c.id) === form.customer_id);
    const account  = cashAccs.find((a: any) => String(a.id) === form.account_id);
    return {
      date: form.date,
      vendor_id: partyType === "vendor" ? (parseInt(form.vendor_id) || null) : null,
      vendor_name: partyType === "vendor" ? vendorName(vendor || {}) : "",
      vendor_currency: partyType === "vendor" ? (vendor?.currency || form.vendor_currency) : (customer?.currency || form.vendor_currency),
      customer_id: partyType === "customer" ? (parseInt(form.customer_id) || null) : null,
      customer_name: partyType === "customer" ? `${customer?.first_name||""} ${customer?.last_name||""} ${customer?.company_name||""}`.trim() : "",
      customer_currency: customer?.currency || form.vendor_currency,
      payment_currency: form.payment_currency,
      payment_amount: parseFloat(form.payment_amount),
      exchange_rate: parseFloat(form.exchange_rate || "1"),
      vendor_amount: vendAmt,
      account_id: parseInt(form.account_id),
      account_name: account?.name || form.account_name,
      notes: form.notes, attachment: form.attachment,
      status: statusValue,
    };
  };

  const resetForm = () => setForm({ date: todayGregorian(), vendor_id: "", customer_id: "", vendor_currency: "USD", payment_currency: "USD", payment_amount: "", exchange_rate: "1", account_id: "", account_name: "", notes: "", attachment: "" } as any);

  const handleSubmitConfirmed = async () => {
    if (!validate()) return;
    const res = await journalAPI.addPaymentOut(buildSubmitData("confirmed"));
    if (res.success) {
      showToast(`پرداخت ${res.serial} با موفقیت ثبت شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const handleSubmitPending = async () => {
    if (!validate()) return;
    const res = await journalAPI.addPaymentOut(buildSubmitData("pending"));
    if (res.success) {
      showToast(`پرداخت ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const openEdit = (p: any) => {
    if (p.customer_id) setPartyType("customer"); else setPartyType("vendor");
    setEditItem({
      id: p.id, date: p.date,
      vendor_id: p.vendor_id ? String(p.vendor_id) : "",
      customer_id: p.customer_id ? String(p.customer_id) : "",
      vendor_currency: p.vendor_currency,
      payment_currency: p.payment_currency,
      payment_amount: String(p.payment_amount),
      exchange_rate: String(p.exchange_rate || "1"),
      account_id: String(p.account_id), account_name: p.account_name,
      notes: p.notes || "", attachment: p.attachment || "",
      status: p.status || "confirmed",
    });
  };

  const handleEdit = async () => {
    if (partyType === "vendor" && !editItem.vendor_id) { showToast("فروشنده الزامی است", false); return; }
    if (partyType === "customer" && !editItem.customer_id) { showToast("مشتری الزامی است", false); return; }
    if (!editItem.payment_amount) { showToast("مبلغ الزامی است", false); return; }
    if (!editItem.account_id)     { showToast("حساب الزامی است", false); return; }
    const vendor   = vendors.find((v: any) => String(v.id) === editItem.vendor_id);
    const customer = customers.find((c: any) => String(c.id) === editItem.customer_id);
    const account  = cashAccs.find((a: any) => String(a.id) === editItem.account_id);
    const res = await journalAPI.updatePaymentOut({
      id: editItem.id, date: editItem.date,
      vendor_id: partyType === "vendor" ? (parseInt(editItem.vendor_id) || null) : null,
      vendor_name: partyType === "vendor" ? vendorName(vendor || {}) : `${customer?.first_name||""} ${customer?.last_name||""} ${customer?.company_name||""}`.trim(),
      vendor_currency: partyType === "vendor" ? (vendor?.currency || editItem.vendor_currency) : (customer?.currency || editItem.vendor_currency),
      customer_id: partyType === "customer" ? (parseInt(editItem.customer_id) || null) : null,
      payment_currency: editItem.payment_currency,
      payment_amount: parseFloat(editItem.payment_amount),
      exchange_rate: parseFloat(editItem.exchange_rate || "1"),
      vendor_amount: editVendAmt,
      account_id: parseInt(editItem.account_id),
      account_name: account?.name || editItem.account_name,
      notes: editItem.notes, attachment: editItem.attachment,
    });
    if (res.success) { showToast("پرداخت ویرایش شد"); setEditItem(null); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف پرداخت ${serial}؟`)) return;
    const res = await journalAPI.deletePaymentOut(id);
    if (res.success) { showToast("پرداخت حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی پرداخت ${serial}؟\nموجودی حساب و بیلانس فروشنده/مشتری به‌روزرسانی می‌شود.`)) return;
    const res = await journalAPI.confirmPaymentOut(id);
    if (res.success) { showToast("پرداخت تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن پرداخت ${serial} به حالت در انتظار؟\nتأثیر مالی آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertPaymentOut(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };


  const filtersLabel = describeFilter(filter, { counterpartyLabel: "طرف حساب", counterparty: [...vendors.map((v: any) => ({ value: `v-${v.id}`, label: partyName2(v) })), ...customers.map((c: any) => ({ value: `c-${c.id}`, label: partyName2(c) }))], account: cashAccs.map((a: any) => ({ value: String(a.id), label: a.name })) });

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در پرداخت‌ها... (شماره سند، نام فروشنده، توضیحات)",
          showDateRange: true,
          showAmountRange: true,
          showCurrency: true,
          showCounterparty: true,
          counterpartyLabel: "طرف حساب",
          counterpartyOptions: [
            ...vendors.map((v: any) => ({ value: `v-${v.id}`, label: `🏪 ${partyName2(v)}` })),
            ...customers.map((c: any) => ({ value: `c-${c.id}`, label: `🤝 ${partyName2(c)}` })),
          ],
          showAccount: true,
          accountOptions: cashAccs.map((a: any) => ({ value: String(a.id), label: a.name })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={list.length}
        totalCount={totalAll}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        T={T}
        dark={dark}
        onReport={() => setShowReport(true)}
        reportLabel="گزارش PDF / Excel پرداخت پول"
      />

      {/* فیلتر وضعیت + دکمه ثبت */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={pendingCount + confirmedCount}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
        />
        {canAdd && <button onClick={() => setShowForm(true)} style={addBtnSt("#f97316")}><Plus size={15} /> ثبت پرداخت پول</button>}
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "68vh", overflowY: "auto" }}>
          {list.length === 0 && <Empty T={T} text="هیچ پرداختی ثبت نشده" />}
          {/* ردیف هدر */}
          {list.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px 1fr 110px 1fr 1.2fr 200px 200px",
              gap: 12,
              padding: "10px 16px",
              borderRadius: 10,
              background: dark ? "#1e293b" : "#f1f5f9",
              position: "sticky" as const, top: 0, zIndex: 2,
              border: `1px solid ${T.cardBorder}`,
              fontSize: 11,
              fontWeight: 700,
              color: T.subtext,
            }}>
              <div>شماره سند</div>
              <div>فروشنده / گیرنده</div>
              <div>تاریخ</div>
              <div>حساب</div>
              <div>شرح</div>
              <div style={{ textAlign: "left" as const }}>مبلغ پرداختی</div>
              <div style={{ textAlign: "center" as const }}>عملیات</div>
            </div>
          )}
          {list.map((p: any) => {
            const isPending = (p.status || "confirmed") === "pending";
            return (
            <div key={p.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {}),
              display: "grid",
              gridTemplateColumns: "130px 1fr 110px 1fr 1.2fr 200px 200px",
              gap: 12,
              alignItems: "center",
            }}>
              {/* ستون 1: سریال + بدج */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                <span style={badgeSt("#f97316")}>{p.serial}</span>
                {isPending && <PendingBadge />}
                {p.attachment && <a href={fileUrl(p.attachment)} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "#3b82f6", textDecoration: "none" }}>📎 ضمیمه</a>}
              </div>

              {/* ستون 2: نام فروشنده/گیرنده */}
              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.vendor_name}
              </div>

              {/* ستون 3: تاریخ */}
              <div>
                <DualDate date={p.date} fontSize={12} color={T.subtext} />
              </div>

              {/* ستون 4: حساب */}
              <div style={{ fontSize: 12, color: T.subtext, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
                {p.account_name}
              </div>

              {/* ستون 5: شرح */}
              <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" as const }}>
                {p.notes || "—"}
              </div>

              {/* ستون 6: مبلغ */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                <span style={{ fontWeight: 800, color: "#f97316", fontSize: 14, direction: "ltr" as const }}>-{fmt(p.payment_amount)} {p.payment_currency}</span>
                {p.payment_currency !== p.vendor_currency && <span style={{ fontSize: 11, color: T.subtext, direction: "ltr" as const }}>≈ {fmt(p.vendor_amount)} {p.vendor_currency}</span>}
              </div>

              {/* ستون 7: عملیات */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                {isPending && canEdit && (
                  <ConfirmIconButton onClick={() => handleConfirm(p.id, p.serial)} />
                )}
                {!isPending && canEdit && (
                  <RevertIconButton onClick={() => handleRevert(p.id, p.serial)} />
                )}
                <button onClick={() => {
                  const v = vendors.find((x: any) => x.id === p.vendor_id);
                  printReceiptOut(p, v, v?.balance || 0);
                }} title="چاپ رسید" style={iconBtnSt("#0ea5e9")}><Printer size={13} /></button>
                {canEdit && <button onClick={() => openEdit(p)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                {canDelete && <button onClick={() => handleDelete(p.id, p.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت */}
      {showForm && (
        <Modal title="ثبت پرداخت پول" onClose={() => setShowForm(false)} dark={dark} T={T}>
          {/* Toggle فروشنده / مشتری */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[{v:"vendor",l:"🏪 به فروشنده"},{v:"customer",l:"👤 به مشتری (بازپرداخت)"}].map(t=>(
              <button key={t.v} onClick={()=>setPartyType(t.v as any)} style={{ flex:1, padding:"9px 14px", borderRadius:9, border:`1.5px solid ${partyType===t.v?"#f97316":T.cardBorder}`, background:partyType===t.v?"rgba(249,115,22,0.1)":"transparent", color:partyType===t.v?"#f97316":T.subtext, cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"inherit" }}>
                {t.l}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              {partyType === "vendor" ? (
                <>
                  <Lbl T={T}>فروشنده *</Lbl>
                  <SearchableSelect options={vendors.map((v: any) => ({ value: String(v.id), label: `${vendorName(v)} (${v.currency})` }))} value={form.vendor_id}
                    onChange={v => { const vn = vendors.find((x: any) => String(x.id) === v); setForm((f: any) => ({ ...f, vendor_id: v, vendor_currency: vn?.currency || "USD", customer_id: "" })); }} placeholder="انتخاب فروشنده" />
                </>
              ) : (
                <>
                  <Lbl T={T}>مشتری *</Lbl>
                  <SearchableSelect options={customers.map((c: any) => ({ value: String(c.id), label: `${c.first_name||""} ${c.last_name||""} ${c.company_name||""}`.trim() + ` (${c.currency||"USD"})` }))} value={form.customer_id||""}
                    onChange={v => { const cn = customers.find((x: any) => String(x.id) === v); setForm((f: any) => ({ ...f, vendor_id: "", customer_id: v, vendor_currency: cn?.currency || "USD" })); }} placeholder="انتخاب مشتری" />
                </>
              )}
            </div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setForm((f: any) => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>ارز پرداختی</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={form.payment_currency} onChange={v => setForm((f: any) => ({ ...f, payment_currency: v }))} placeholder="انتخاب ارز" /></div>
            <div><Lbl T={T}>مبلغ پرداختی *</Lbl><input type="number" value={form.payment_amount} onChange={e => setForm((f: any) => ({ ...f, payment_amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله ({form.vendor_currency})</Lbl><input type="number" value={form.exchange_rate} onChange={e => setForm((f: any) => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به ارز {partyType==="vendor"?"فروشنده":"مشتری"} ({form.vendor_currency})</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(249,115,22,0.1)" : "#fff7ed", color: "#f97316", fontWeight: 700, fontSize: 14, border: "1.5px solid #f9731620" }}>{fmt(vendAmt)} {form.vendor_currency}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>حساب پرداختی *</Lbl>
              <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={form.account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm((f: any) => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="فقط حساب‌های دارایی نقدی" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setForm((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه (فایل یا صدا)</Lbl><AttachmentField value={form.attachment || ""} fileName={form.attachment ? String(form.attachment).split("/").pop() : ""} onChange={(path) => setForm((f: any) => ({ ...f, attachment: path }))} uploadFn={(file) => journalAPI.uploadAttachment(file)} T={T} dark={dark} /></div>
          </div>
          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره پرداخت در انتظار"
              />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmitConfirmed)}
                loading={submitting}
                color="#f97316"
                label="ثبت پرداخت"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش */}
      {editItem && (
        <Modal title={`ویرایش پرداخت پول${editItem.status==="pending" ? " (در انتظار)" : ""}`} onClose={() => setEditItem(null)} dark={dark} T={T}>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[{v:"vendor",l:"🏪 به فروشنده"},{v:"customer",l:"👤 به مشتری (بازپرداخت)"}].map(t=>(
              <button key={t.v} onClick={()=>setPartyType(t.v as any)} style={{ flex:1, padding:"9px 14px", borderRadius:9, border:`1.5px solid ${partyType===t.v?"#f97316":T.cardBorder}`, background:partyType===t.v?"rgba(249,115,22,0.1)":"transparent", color:partyType===t.v?"#f97316":T.subtext, cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"inherit" }}>
                {t.l}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              {partyType === "vendor" ? (
                <>
                  <Lbl T={T}>فروشنده *</Lbl>
                  <SearchableSelect options={vendors.map((v: any) => ({ value: String(v.id), label: `${vendorName(v)} (${v.currency})` }))} value={editItem.vendor_id}
                    onChange={v => { const vn = vendors.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, vendor_id: v, customer_id: "", vendor_currency: vn?.currency || "USD" })); }} placeholder="انتخاب فروشنده" />
                </>
              ) : (
                <>
                  <Lbl T={T}>مشتری *</Lbl>
                  <SearchableSelect options={customers.map((c: any) => ({ value: String(c.id), label: `${c.first_name||""} ${c.last_name||""} ${c.company_name||""}`.trim() + ` (${c.currency||"USD"})` }))} value={editItem.customer_id||""}
                    onChange={v => { const cn = customers.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, customer_id: v, vendor_id: "", vendor_currency: cn?.currency || "USD" })); }} placeholder="انتخاب مشتری" />
                </>
              )}
            </div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>ارز پرداختی</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={editItem.payment_currency} onChange={v => setEditItem((f: any) => ({ ...f, payment_currency: v }))} placeholder="انتخاب ارز" /></div>
            <div><Lbl T={T}>مبلغ پرداختی *</Lbl><input type="number" value={editItem.payment_amount} onChange={e => setEditItem((f: any) => ({ ...f, payment_amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله ({editItem.vendor_currency})</Lbl><input type="number" value={editItem.exchange_rate} onChange={e => setEditItem((f: any) => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به ارز {partyType==="vendor"?"فروشنده":"مشتری"} ({editItem.vendor_currency})</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(249,115,22,0.1)" : "#fff7ed", color: "#f97316", fontWeight: 700, fontSize: 14, border: "1.5px solid #f9731620" }}>{fmt(editVendAmt)} {editItem.vendor_currency}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>حساب پرداختی *</Lbl>
              <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={editItem.account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="انتخاب حساب" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>ضمیمه (فایل یا صدا)</Lbl><AttachmentField value={editItem.attachment || ""} fileName={editItem.attachment ? String(editItem.attachment).split("/").pop() : ""} onChange={(path) => setEditItem((f: any) => ({ ...f, attachment: path }))} uploadFn={(file) => journalAPI.uploadAttachment(file)} T={T} dark={dark} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#f97316" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}
      {showReport && (
        <TxnReportModal type="payments_out" title="پرداخت پول" filter={filter} filtersLabel={filtersLabel} onClose={() => setShowReport(false)} T={T} dark={dark} />
      )}
    </div>
  );
}